// Consume an NDJSON streaming response from the ai-* Function URLs. Every
// `useGenerate*` hook re-implemented the same loop: POST, read the body line by
// line, resolve on {type:'complete'}, throw on {type:'error'} or a truncated
// stream. {type:'chunk'} deltas are the partial text-mode structured-output
// JSON (partial JSON text, not tool-call args); pass
// `onChunk` to receive the accumulated text after each delta (progressive
// proposal rendering), or omit it to ignore them.
//
// The body is read regardless of HTTP status so a server {type:'error'} line
// surfaces its message; only a missing body falls back to the status code.

import { sha256Hex } from '@/lib/request-signing';

interface NdjsonMessage<T> {
  type?: 'chunk' | 'complete' | 'error';
  text?: string;
  data?: T;
  message?: string;
}

export async function consumeNdjsonStream<T>(
  url: string,
  body: unknown,
  headers: Record<string, string> = {},
  onChunk?: (accumulated: string) => void,
): Promise<T> {
  const bodyStr = JSON.stringify(body);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      ...headers,
      // Computed values win over caller headers — a caller-supplied hash or
      // Content-Type would break OAC SigV4 validation (opaque 403s).
      'Content-Type': 'application/json',
      'x-amz-content-sha256': await sha256Hex(bodyStr),
    },
    body: bodyStr,
  });
  if (!response.body) throw new Error(`HTTP ${response.status}`);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let accumulated = '';
  let result: T | null = null;
  let streamError: string | null = null;

  const consume = (line: string) => {
    if (!line.trim()) return;
    let msg: NdjsonMessage<T>;
    try {
      msg = JSON.parse(line);
    } catch {
      return; // partial / non-JSON line — skip
    }
    // A {type:'complete'} with no data defaults to {}, not null, so a complete
    // line always produces a result — callers read it as `data.x ?? []`. That
    // keeps `result === null` below meaning "stream ended without a complete
    // line" (truncated), matching the old per-hook `msg.data?.x ?? []`.
    if (msg.type === 'complete') result = (msg.data ?? {}) as T;
    else if (msg.type === 'error') streamError = msg.message || 'Request failed';
    else if (msg.type === 'chunk' && onChunk && typeof msg.text === 'string') {
      accumulated += msg.text;
      // Previews are decoration: a throwing onChunk must not reject a stream
      // that will still deliver a valid complete line.
      try {
        onChunk(accumulated);
      } catch {
        // ignore — the complete line is the payload
      }
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) consume(line);
  }
  buffer += decoder.decode();
  consume(buffer);

  if (streamError) throw new Error(streamError);
  if (result === null) {
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    throw new Error('Stream did not complete');
  }
  return result;
}
